import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  GoldenRewardApi,
  diamondRewardApi,
  userspinApi,
} from "../../http/http";
import { isFresh } from "../cacheUtils";

const SPIN_TTL_MS = 1000 * 60 * 5; // 5 min
const GOLDEN_TTL_MS = 1000 * 60 * 5; // 5 min
const DIAMOND_TTL_MS = 1000 * 60 * 5; // 5 min

export const fetchSpinHistory = createAsyncThunk(
  "rewards/fetchSpinHistory",
  async (token) => {
    if (!token) throw new Error("Missing auth token");
    const res = await userspinApi(token);
    return res?.data?.result || [];
  },
  {
    condition: (token, { getState }) => {
      const state = getState().rewards;
      if (!token) return false;
      if (state.token && state.token !== token) return true;
      return !isFresh(state.spinLastFetched, SPIN_TTL_MS);
    },
  }
);

export const fetchGoldenReward = createAsyncThunk(
  "rewards/fetchGoldenReward",
  async (token) => {
    if (!token) throw new Error("Missing auth token");
    const res = await GoldenRewardApi(token);
    return res?.data?.result?.[0] || null;
  },
  {
    condition: (token, { getState }) => {
      const state = getState().rewards;
      if (!token) return false;
      if (state.token && state.token !== token) return true;
      return !isFresh(state.goldenLastFetched, GOLDEN_TTL_MS);
    },
  }
);

export const fetchDiamondRewards = createAsyncThunk(
  "rewards/fetchDiamondRewards",
  async (token) => {
    if (!token) throw new Error("Missing auth token");
    const res = await diamondRewardApi(token);
    return res?.data || [];
  },
  {
    condition: (token, { getState }) => {
      const state = getState().rewards;
      if (!token) return false;
      if (state.token && state.token !== token) return true;
      return !isFresh(state.diamondLastFetched, DIAMOND_TTL_MS);
    },
  }
);

const rewardsSlice = createSlice({
  name: "rewards",
  initialState: {
    token: null,

    spinHistory: [],
    spinStatus: "idle",
    spinError: null,
    spinLastFetched: null,

    goldenReward: null,
    goldenStatus: "idle",
    goldenError: null,
    goldenLastFetched: null,

    diamondRewards: [],
    diamondStatus: "idle",
    diamondError: null,
    diamondLastFetched: null,
  },
  reducers: {
    clearRewards(state) {
      state.token = null;
      state.spinHistory = [];
      state.spinStatus = "idle";
      state.spinError = null;
      state.spinLastFetched = null;

      state.goldenReward = null;
      state.goldenStatus = "idle";
      state.goldenError = null;
      state.goldenLastFetched = null;

      state.diamondRewards = [];
      state.diamondStatus = "idle";
      state.diamondError = null;
      state.diamondLastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSpinHistory.pending, (state) => {
        state.spinStatus = "loading";
        state.spinError = null;
      })
      .addCase(fetchSpinHistory.fulfilled, (state, action) => {
        state.spinStatus = "succeeded";
        state.spinHistory = action.payload;
        state.token = state.token || action.meta.arg;
        state.spinLastFetched = Date.now();
      })
      .addCase(fetchSpinHistory.rejected, (state, action) => {
        state.spinStatus = "failed";
        state.spinError = action.error?.message || "Failed to load spin history";
      })
      .addCase(fetchGoldenReward.pending, (state) => {
        state.goldenStatus = "loading";
        state.goldenError = null;
      })
      .addCase(fetchGoldenReward.fulfilled, (state, action) => {
        state.goldenStatus = "succeeded";
        state.goldenReward = action.payload;
        state.token = state.token || action.meta.arg;
        state.goldenLastFetched = Date.now();
      })
      .addCase(fetchGoldenReward.rejected, (state, action) => {
        state.goldenStatus = "failed";
        state.goldenError = action.error?.message || "Failed to load golden reward";
      })
      .addCase(fetchDiamondRewards.pending, (state) => {
        state.diamondStatus = "loading";
        state.diamondError = null;
      })
      .addCase(fetchDiamondRewards.fulfilled, (state, action) => {
        state.diamondStatus = "succeeded";
        state.diamondRewards = action.payload;
        state.token = state.token || action.meta.arg;
        state.diamondLastFetched = Date.now();
      })
      .addCase(fetchDiamondRewards.rejected, (state, action) => {
        state.diamondStatus = "failed";
        state.diamondError = action.error?.message || "Failed to load diamond rewards";
      });
  },
});

export const { clearRewards } = rewardsSlice.actions;
export default rewardsSlice.reducer;

